"use client";

type DesktopInvoke = <T = unknown>(
  command: string,
  args?: Record<string, unknown>,
) => Promise<T>;

type DesktopWindow = Window & {
  __TAURI_INTERNALS__?: {
    invoke?: DesktopInvoke;
  };
  __FINDIN_DESKTOP_CAPABILITIES__?: {
    zoteroLibrary?: boolean;
  };
};

export type ZoteroStatus = {
  running: boolean;
  apiEnabled: boolean;
  version?: string;
  apiVersion?: string;
  message: string;
};

export type ZoteroCollection = {
  key: string;
  name: string;
  parentCollection?: string;
};

export type ZoteroPaper = {
  itemKey: string;
  itemVersion: number;
  attachmentKey: string;
  attachmentVersion: number;
  filename: string;
  title: string;
  creators: string[];
  date: string;
  doi: string;
  abstractNote: string;
  publicationTitle: string;
  collections: string[];
  dateModified: string;
};

export type ZoteroLibrary = {
  collections: ZoteroCollection[];
  papers: ZoteroPaper[];
};

export type ZoteroHashCandidate = {
  paperId: string;
  md5: string;
  sha1: string;
};

export type ZoteroHashMatch = ZoteroHashCandidate & {
  itemKey: string;
  attachmentKey: string;
};

function getDesktopInvoke(): DesktopInvoke | null {
  if (typeof window === "undefined") {
    return null;
  }

  const invoke = (window as DesktopWindow).__TAURI_INTERNALS__?.invoke;
  return typeof invoke === "function" ? invoke : null;
}

function requireDesktopInvoke(): DesktopInvoke {
  const invoke = getDesktopInvoke();
  if (!invoke) {
    throw new Error("Zotero 导入仅支持 Findin 桌面端。");
  }
  return invoke;
}

async function invokeWithTimeout<T>(
  command: string,
  args: Record<string, unknown> | undefined,
  timeoutMs: number,
) {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      requireDesktopInvoke()<T>(command, args),
      new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new Error("连接 Zotero 超时，请确认 Zotero 正在运行后重试。"));
        }, timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
}

export function isZoteroDesktopRuntime() {
  if (typeof window === "undefined") {
    return false;
  }
  const desktopWindow = window as DesktopWindow;
  return getDesktopInvoke() !== null
    && desktopWindow.__FINDIN_DESKTOP_CAPABILITIES__?.zoteroLibrary === true;
}

export async function getZoteroStatus() {
  return invokeWithTimeout<ZoteroStatus>("get_zotero_status", undefined, 10_000);
}

export async function listZoteroPapers() {
  return invokeWithTimeout<ZoteroLibrary>("list_zotero_papers", undefined, 30_000);
}

export async function openZoteroItem(itemKey: string) {
  return invokeWithTimeout<void>("open_zotero_item", { itemKey }, 10_000);
}

export async function matchZoteroPapers(candidates: ZoteroHashCandidate[]) {
  return invokeWithTimeout<ZoteroHashMatch[]>(
    "match_zotero_papers",
    { candidates },
    120_000,
  );
}

function toUint8Array(value: ArrayBuffer | Uint8Array | number[]) {
  if (value instanceof Uint8Array) {
    return value;
  }
  if (value instanceof ArrayBuffer) {
    return new Uint8Array(value);
  }
  if (Array.isArray(value)) {
    return new Uint8Array(value);
  }
  throw new Error("无法解析 Zotero PDF 数据。");
}

export async function readZoteroPaperFile(paper: ZoteroPaper) {
  const data = await invokeWithTimeout<ArrayBuffer | Uint8Array | number[]>(
    "read_zotero_attachment",
    { attachmentKey: paper.attachmentKey },
    60_000,
  );
  const bytes = toUint8Array(data);
  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);
  return new File([buffer], paper.filename || `${paper.title}.pdf`, {
    type: "application/pdf",
    lastModified: Date.parse(paper.dateModified) || Date.now(),
  });
}
